import { IRedditMentionRepository } from '../../domain/interfaces/IRedditMentionRepository';
import {
  RedditMention,
  RedditMentionDto,
  GetAllMentionsQuery,
  ConfirmMatchCommand,
  RejectMatchCommand,
  UpdateMentionCommand,
  MappedBusinessMentionsResponse
} from '../../domain/entities/RedditMention';
import { PaginatedResult } from '../../domain/common/PaginatedResult';
import { UserRole } from '../../domain/value-objects/UserRole';

/**
 * RedditMentionService - Application service for Reddit mention operations
 *
 * Orchestrates complex mention operations and enforces business rules
 */
export class RedditMentionService {
  constructor(private readonly mentionRepository: IRedditMentionRepository) {}

  /**
   * Get mentions for a user based on their role
   * - Admin: All mentions (returns PaginatedResult)
   * - BusinessOwner: Only mentions for their business (returns MappedBusinessMentionsResponse)
   *
   * Security: Backend enforces all authorization. Frontend only provides UX.
   * Development mode includes assertions to catch backend security regressions.
   */
  async getMentionsForUser(
    userRole: UserRole,
    userBusinessId: string | null,
    query?: GetAllMentionsQuery
  ): Promise<PaginatedResult<RedditMentionDto> | MappedBusinessMentionsResponse> {
    // Admin can see all mentions
    if (userRole === 'Admin') {
      return this.mentionRepository.getAll(query);
    }

    // BusinessOwner can only see their business mentions
    if (userRole === 'BusinessOwner') {
      if (!userBusinessId) {
        throw new Error('BusinessOwner must have a businessId');
      }

      const result = await this.mentionRepository.getByBusiness(userBusinessId, query);

      // ✅ Security note: The /business/{businessId} endpoint enforces filtering by URL parameter
      // Backend validates businessId ownership and returns 403 if unauthorized
      // RedditMentionSummary doesn't include businessId field, so no client-side validation needed

      return result;
    }

    throw new Error(`Unknown user role: ${userRole}`);
  }

  /**
   * Get a single mention by ID with role-based access
   *
   * Security: Backend enforces authorization and returns 403 if unauthorized.
   * Frontend only handles the response gracefully.
   */
  async getMentionById(
    id: string,
    userRole: UserRole,
    _userBusinessId: string | null
  ): Promise<RedditMention> {
    // Backend enforces authorization - will return 403 if BusinessOwner
    // tries to access another business's mention
    const mention = await this.mentionRepository.getById(id);

    // Admin can see any mention
    if (userRole === 'Admin') {
      return mention;
    }

    // For BusinessOwner, backend already validated ownership
    // If we got here, the mention belongs to their business
    return mention;
  }

  /**
   * Process mention verification with business logic
   * - Validates mention exists
   * - Confirms the match
   * - Returns updated mention
   */
  async processMentionVerification(
    mentionId: string,
    businessId: string,
    userRole: UserRole
  ): Promise<void> {
    // Verify user has permission
    if (userRole !== 'Admin') {
      throw new Error('Only admins can verify mentions');
    }

    // Confirm the match
    const command: ConfirmMatchCommand = {
      mentionId,
      businessId
    };

    await this.mentionRepository.confirmMatch(command);
  }

  /**
   * Reject a mention match
   */
  async rejectMention(mentionId: string, reason: string | undefined, userRole: UserRole): Promise<void> {
    // Verify user has permission
    if (userRole !== 'Admin') {
      throw new Error('Only admins can reject mentions');
    }

    const command: RejectMatchCommand = {
      mentionId,
      reason
    };

    await this.mentionRepository.rejectMatch(command);
  }

  /**
   * Batch process multiple mentions
   * - Admin only operation
   * - Validates all mentions before processing
   */
  async batchProcessMentions(
    mentionIds: string[],
    action: 'confirm' | 'reject',
    businessId?: string,
    reason?: string,
    userRole: UserRole = 'Admin'
  ): Promise<{ processed: number; failed: number; errors: string[] }> {
    // Verify admin permission
    if (userRole !== 'Admin') {
      throw new Error('Only admins can batch process mentions');
    }

    const results = {
      processed: 0,
      failed: 0,
      errors: [] as string[]
    };

    // Process each mention
    for (const mentionId of mentionIds) {
      try {
        if (action === 'confirm') {
          if (!businessId) {
            throw new Error('businessId required for confirm action');
          }
          await this.processMentionVerification(mentionId, businessId, userRole);
        } else {
          await this.rejectMention(mentionId, reason, userRole);
        }
        results.processed++;
      } catch (error) {
        results.failed++;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        results.errors.push(`Mention ${mentionId}: ${errorMessage}`);
      }
    }

    return results;
  }

  /**
   * Update a mention (admin only)
   */
  async updateMention(
    id: string,
    command: UpdateMentionCommand,
    userRole: UserRole
  ): Promise<RedditMention> {
    // Verify admin permission
    if (userRole !== 'Admin') {
      throw new Error('Only admins can update mentions');
    }

    return this.mentionRepository.update(id, command);
  }

  /**
   * Delete a mention (admin only)
   */
  async deleteMention(id: string, userRole: UserRole): Promise<void> {
    // Verify admin permission
    if (userRole !== 'Admin') {
      throw new Error('Only admins can delete mentions');
    }

    await this.mentionRepository.delete(id);
  }

  /**
   * Create a new mention (admin only)
   */
  async createMention(
    mention: Partial<RedditMention>,
    userRole: UserRole
  ): Promise<RedditMention> {
    // Verify admin permission
    if (userRole !== 'Admin') {
      throw new Error('Only admins can create mentions');
    }

    // Validate required fields
    if (!mention.redditPostId || !mention.subreddit || !mention.postTitle || !mention.content) {
      throw new Error('Missing required fields: redditPostId, subreddit, postTitle, content');
    }

    return this.mentionRepository.create(mention);
  }

  /**
   * Recalculate match confidence for a mention (admin only)
   */
  async recalculateConfidence(id: string, userRole: UserRole): Promise<RedditMention> {
    // Verify admin permission
    if (userRole !== 'Admin') {
      throw new Error('Only admins can recalculate match confidence');
    }

    return this.mentionRepository.recalculateMatchConfidence(id);
  }

  /**
   * Process unmatched mentions (admin only)
   */
  async processUnmatched(userRole: UserRole): Promise<void> {
    // Verify admin permission
    if (userRole !== 'Admin') {
      throw new Error('Only admins can process unmatched mentions');
    }

    await this.mentionRepository.processUnmatched();
  }
}
